import yaml from "js-yaml";
import { useQuery } from "@tanstack/react-query";

import { getEnvironmentService } from "../../backend";
import { TEMPLATES } from "../../enums/enums";
import type { Template } from "../../types";
import { getTemplate } from "../../utils/get-template";
import type { InfraTemplate } from "./addFlow";

const LOGOS = "https://microtica.s3.eu-central-1.amazonaws.com/assets/templates/logos";
// The logos the templates themselves declare, so the list needn't fetch every template
const LOGO: Record<string, string> = {
    nodejs: "nodejs.png", nextjs: "nextjs.png", reactjs: "reactjs.png", appwrite: "appwrite.svg", nuxtjs: "nuxtjs.svg",
    angular: "angularjs.png", vuejs: "vuejs.png", svelte: "svelte.png", gatsby: "gatsby.png", nestjs: "nestjs.svg",
    gin: "gin.png", iris: "iris.png", django4: "django.png",
};

export interface Starter {
    key: string;
    title: string;
    description: string;
    /** The template.yaml */
    link: string;
    cloudProvider?: string;
    /** Infrastructure starters only run in environments with this tool */
    iac?: string;
    logo?: string;
}

/** The import template: its inputs name the service, its files go into the imported repository */
export const IMPORT_TEMPLATE_LINK = TEMPLATES.find(t => t.key === "import-app")!.link!.split("&")[0];

/**
 * Microtica's free starters that run on Kubernetes; each creates a repository with a working
 * app. Premium ones keep the classic flow, which also handles the plan upgrade.
 */
export const STARTERS: Starter[] = TEMPLATES
    .filter(t => t.type === "app" && t.deploymentTarget === "kubernetes" && t.pack === "free" && !!t.link)
    .map(t => ({
        key: t.key,
        title: t.title as string,
        description: t.description as string,
        link: t.link!,
        cloudProvider: t.cloudProvider,
        logo: LOGO[t.key] && `${LOGOS}/${LOGO[t.key]}`,
    }));

/**
 * Microtica's infrastructure templates: each creates its resources in the environment (and,
 * for some, a repository with their code), then deploys them in one template run.
 */
export const INFRA_STARTERS: Starter[] = TEMPLATES
    .filter(t => t.type === "component" && !!t.link)
    .map(t => ({
        key: t.key,
        title: t.title as string,
        description: t.description as string,
        link: t.link!,
        cloudProvider: t.cloudProvider,
        iac: t.iac,
    }));

/** A template's definition: its inputs, and the repository it comes from */
export function useTemplateYaml(link?: string) {
    return useQuery({
        enabled: !!link,
        queryKey: ["v2", "template-yaml", link],
        queryFn: async () => yaml.load(await getTemplate(link!)) as Template,
        staleTime: Infinity,
    });
}

/** The project's infrastructure templates: Microtica's public components and the team's own */
export function useInfraTemplates(projectId: string) {
    return useQuery({
        queryKey: ["v2", "infra-templates", projectId],
        queryFn: async (): Promise<InfraTemplate[]> => (await getEnvironmentService().getComponents(projectId)).data.components.map(c => ({
            id: c.id,
            name: c.name,
            description: c.description,
            type: c.type,
            isPublic: c.isPublic,
            cloudProvider: c.cloudProvider,
            infrastructureAsCodeTool: c.infrastructureAsCodeTool,
        })),
        staleTime: 5 * 60 * 1000,
    });
}
